import { RefObject, useEffect, useRef } from 'react';

/**
 * Bring a newly expanded disclosure fully into view, and report whether the
 * reveal actually had to move the page.
 *
 * A row near the bottom of a long page expands BELOW the fold: the viewport
 * doesn't move, nothing visible changes, and a first-time user reasonably
 * concludes the click did nothing. Scrolling the opened panel into view is the
 * conventional fix and costs nothing when the panel was already on screen —
 * `block: 'nearest'` is a no-op in that case, and when the panel is taller than
 * the viewport the spec aligns its START edge, so the row header the user just
 * clicked never scrolls away above them.
 *
 * Attach the returned ref to the element that wraps BOTH the trigger row and the
 * panel, and give that element a `scroll-margin-block-end` so the reveal lands
 * with breathing room rather than flush against the viewport edge.
 */
export const useRevealOnExpand = <T extends HTMLElement>(
  /**
   * Truthy while a panel is open. Pass a boolean when the caller renders one row
   * per component, or the expanded row's id when a single parent tracks which of
   * many rows is open — the value doubles as the effect's dependency, so moving
   * from one open row to another re-reveals.
   */
  expanded: boolean | string | null
): RefObject<T | null> => {
  const ref = useRef<T | null>(null);
  // A row that mounts already expanded (restored state, or a remount from a
  // dataset change) must not yank the page around before the user has acted, so
  // the first effect run only records that mount happened.
  const hasMounted = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!hasMounted.current) {
      hasMounted.current = true;
      return undefined;
    }
    // scrollIntoView is absent in jsdom, so feature-detect rather than assume.
    if (!expanded || !el || typeof el.scrollIntoView !== 'function') {
      return undefined;
    }
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // One frame, so the panel is laid out and its real height is known before we
    // decide how far to scroll.
    const frame = requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [expanded]);

  return ref;
};
